import {memo} from 'react'
import {Mic, MicOff, Video, VideoOff} from 'lucide-react'
import {Spinner} from '@/shared/ui'
import {cn} from '@/shared/lib/cn'
import {MediaToggleSplitButton} from '@/pages/room/device-selector'
import {AudioSettingsPopover} from '@/pages/room/audio-settings'
import type {InputSensitivityMode} from '@/pages/room/audio-settings'

interface VideoPreviewSectionProps {
  isLoading: boolean
  isVideoLoading: boolean
  isVideoEnabled: boolean
  isAudioEnabled: boolean
  hasVideo: boolean
  hasAudio: boolean
  isFrontCamera: boolean
  videoRefCallback: (element: HTMLVideoElement | null) => void
  onToggleAudio: () => void
  onToggleVideo: () => void
  audioDevices: MediaDeviceInfo[]
  selectedAudioDeviceId: string | null
  onSelectAudioDevice: (deviceId: string) => void
  videoDevices: MediaDeviceInfo[]
  selectedVideoDeviceId: string | null
  onSelectVideoDevice: (deviceId: string) => void
  /** Real-time mic audio level for the settings meter */
  audioLevel: number
  /** Auto-computed threshold from the audio worklet */
  audioAutoThreshold: number
  /** Current noise gate threshold */
  audioThreshold: number
  /** Current sensitivity mode */
  audioMode: InputSensitivityMode
  /** Called when threshold changes */
  onAudioThresholdChange: (value: number) => void
  /** Called when mode changes */
  onAudioModeChange: (mode: InputSensitivityMode) => void
}

export const VideoPreviewSection = memo(function VideoPreviewSection({
  isLoading,
  isVideoLoading,
  isVideoEnabled,
  isAudioEnabled,
  hasVideo,
  hasAudio,
  isFrontCamera,
  videoRefCallback,
  onToggleAudio,
  onToggleVideo,
  audioDevices,
  selectedAudioDeviceId,
  onSelectAudioDevice,
  videoDevices,
  selectedVideoDeviceId,
  onSelectVideoDevice,
  audioLevel,
  audioAutoThreshold,
  audioThreshold,
  audioMode,
  onAudioThresholdChange,
  onAudioModeChange,
}: VideoPreviewSectionProps) {
  const showVideo = isVideoEnabled && hasVideo && !isVideoLoading

  return (
    <div className="mb-6">
      <div className="relative aspect-4/3 overflow-hidden rounded-2xl bg-muted">
        {isVideoLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-foreground/70">
            <Spinner size="medium" />
            <p>Подключение камеры…</p>
          </div>
        ) : (
          <>
            <video
              ref={videoRefCallback}
              autoPlay
              playsInline
              muted
              className={cn('size-full object-cover', isFrontCamera && '-scale-x-100')}
              style={{display: showVideo ? 'block' : 'none'}}
            />
            {!showVideo && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-muted">
                <p className="text-sm text-muted-foreground">Камера выключена</p>
              </div>
            )}
          </>
        )}

        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center justify-center gap-3">
          <MediaToggleSplitButton
            isEnabled={isAudioEnabled}
            onToggle={onToggleAudio}
            devices={audioDevices}
            selectedDeviceId={selectedAudioDeviceId}
            onSelectDevice={onSelectAudioDevice}
            icon={<Mic />}
            disabledIcon={<MicOff />}
            toggleLabel={
              !hasAudio
                ? 'Микрофон не найден'
                : isAudioEnabled
                  ? 'Выключить микрофон'
                  : 'Включить микрофон'
            }
            pickerLabel="Выбрать микрофон"
            deviceFallbackName="Микрофон"
            disabled={isLoading || !hasAudio}
            size="medium"
          />
          <MediaToggleSplitButton
            isEnabled={isVideoEnabled}
            onToggle={onToggleVideo}
            devices={videoDevices}
            selectedDeviceId={selectedVideoDeviceId}
            onSelectDevice={onSelectVideoDevice}
            icon={<Video />}
            disabledIcon={<VideoOff />}
            toggleLabel={
              !hasVideo
                ? 'Камера не найдена'
                : isVideoEnabled
                  ? 'Выключить камеру'
                  : 'Включить камеру'
            }
            pickerLabel="Выбрать камеру"
            deviceFallbackName="Камера"
            disabled={isLoading || isVideoLoading || !hasVideo}
            size="medium"
          />
          <AudioSettingsPopover
            level={audioLevel}
            autoThreshold={audioAutoThreshold}
            threshold={audioThreshold}
            mode={audioMode}
            size="medium"
            onThresholdChange={onAudioThresholdChange}
            onModeChange={onAudioModeChange}
          />
        </div>
      </div>
    </div>
  )
})
